/** Глобальный плеер голосовых в чате: audio на document.body, не прерывается при оверлеях/размонтировании UI. */

const SPEED_STEPS = [0.5, 1, 1.2, 1.5, 1.7, 2];

let audioEl = null;
let activeKey = null;
let sourceUrl = null;
let loadStatus = "idle"; // idle | loading | ready
let speed = 1;
let pendingPlay = false;
let pendingResume = null;
let loadGen = 0;
let progressRafId = null;

/** key -> blob: URL (кэш префетча) */
const blobCache = new Map();
/** key -> duration sec */
const durationByKey = new Map();
const prefetching = new Map();

const listeners = new Set();

function notify() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      // ignore
    }
  });
}

function stopProgressLoop() {
  if (progressRafId != null) {
    cancelAnimationFrame(progressRafId);
    progressRafId = null;
  }
}

function startProgressLoop() {
  if (progressRafId != null) return;
  const loop = () => {
    const el = audioEl;
    if (!el || el.paused || !activeKey) {
      progressRafId = null;
      return;
    }
    notify();
    progressRafId = requestAnimationFrame(loop);
  };
  progressRafId = requestAnimationFrame(loop);
}

function probeDuration(key, url) {
  const k = String(key);
  if (durationByKey.has(k) || !url) return;
  const probe = document.createElement("audio");
  probe.preload = "metadata";
  const cleanup = () => {
    probe.removeAttribute("src");
    try {
      probe.load();
    } catch {
      // ignore
    }
  };
  probe.addEventListener("loadedmetadata", () => {
    rememberDuration(k, probe.duration);
    notify();
    cleanup();
  });
  probe.addEventListener("error", cleanup);
  probe.src = url;
}

function rememberDuration(key, seconds) {
  if (!key || !Number.isFinite(seconds) || seconds <= 0) return;
  durationByKey.set(String(key), seconds);
}

function syncDurationFromElement() {
  const el = audioEl;
  if (!el || !activeKey || !Number.isFinite(el.duration) || el.duration <= 0) return;
  rememberDuration(activeKey, el.duration);
}

function ensureAudio() {
  if (!audioEl) {
    audioEl = document.createElement("audio");
    audioEl.preload = "auto";
    audioEl.setAttribute("playsinline", "");
    audioEl.setAttribute("webkit-playsinline", "");
    // Не уводим далеко за экран — в WebView иначе часто не приходит timeupdate.
    audioEl.style.cssText =
      "position:fixed;left:0;bottom:0;width:1px;height:1px;opacity:0;pointer-events:none;z-index:-1";
    document.body.appendChild(audioEl);
    audioEl.addEventListener("play", () => {
      notify();
      startProgressLoop();
    });
    audioEl.addEventListener("pause", () => {
      stopProgressLoop();
      notify();
    });
    audioEl.addEventListener("ended", () => {
      stopProgressLoop();
      activeKey = null;
      pendingPlay = false;
      notify();
    });
    audioEl.addEventListener("timeupdate", notify);
    audioEl.addEventListener("loadedmetadata", () => {
      syncDurationFromElement();
      if (activeKey && loadStatus === "loading") {
        loadStatus = "ready";
      }
      notify();
    });
    audioEl.addEventListener("durationchange", () => {
      syncDurationFromElement();
      notify();
    });
    audioEl.addEventListener("canplay", () => {
      syncDurationFromElement();
      if (activeKey && loadStatus === "loading") {
        loadStatus = "ready";
      }
      const el = audioEl;
      const pending = pendingResume;
      if (!el || !pending) return;
      pendingResume = null;
      try {
        el.currentTime = pending.time || 0;
      } catch {
        // ignore
      }
      if (pending.shouldPlay) {
        el.play().catch(() => {});
      }
      notify();
    });
  }
  return audioEl;
}

async function fetchBlobUrl(key, src) {
  const k = String(key);
  if (blobCache.has(k)) return blobCache.get(k);
  if (prefetching.has(k)) return prefetching.get(k);

  const promise = (async () => {
    if (!src) return "";
    try {
      const res = await fetch(src);
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      blobCache.set(k, u);
      probeDuration(k, u);
      return u;
    } catch {
      probeDuration(k, src);
      return src;
    } finally {
      prefetching.delete(k);
    }
  })();

  prefetching.set(k, promise);
  return promise;
}

export function subscribeVoicePlayback(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getVoicePlaybackSnapshot(key) {
  const k = String(key);
  const el = audioEl;
  const isActive = activeKey != null && String(activeKey) === k;
  const cachedDuration = durationByKey.get(k) || 0;
  const liveDuration =
    isActive && el && Number.isFinite(el.duration) && el.duration > 0 ? el.duration : cachedDuration;
  return {
    isActive,
    playing: Boolean(isActive && el && !el.paused),
    currentTime: isActive && el ? el.currentTime || 0 : 0,
    duration: liveDuration,
    speed,
    loadStatus: isActive
      ? loadStatus
      : blobCache.has(k) || prefetching.has(k)
        ? "ready"
        : "idle",
    activeKey,
  };
}

export function prefetchVoice(key, src) {
  const k = String(key);
  if (!src || blobCache.has(k) || prefetching.has(k)) return;
  void fetchBlobUrl(k, src);
}

export async function prepareAndPlayVoice(key, src) {
  const k = String(key);
  if (!src) return;

  if (activeKey === k && sourceUrl === src && loadStatus === "ready") {
    const el = ensureAudio();
    if (el.paused) {
      el.playbackRate = speed;
      el.play().catch(() => {});
    } else {
      el.pause();
    }
    return;
  }

  if (activeKey === k && sourceUrl === src && loadStatus === "loading") {
    pendingPlay = true;
    return;
  }

  const el = ensureAudio();
  el.pause();
  activeKey = k;
  sourceUrl = src;
  loadStatus = "loading";
  pendingPlay = true;
  pendingResume = null;
  notify();

  const gen = ++loadGen;
  const playUrl = await fetchBlobUrl(k, src);
  if (gen !== loadGen || activeKey !== k) return;

  el.playbackRate = speed;
  if (pendingPlay) {
    pendingResume = { time: 0, shouldPlay: true };
    pendingPlay = false;
  }
  el.src = playUrl;
  notify();
}

export function toggleVoicePlayback(key, src) {
  const k = String(key);
  const el = audioEl;
  if (activeKey === k && sourceUrl === src && loadStatus === "ready" && el) {
    if (el.paused) {
      el.playbackRate = speed;
      el.play().catch(() => {});
    } else {
      el.pause();
    }
    return;
  }
  void prepareAndPlayVoice(k, src);
}

export function stopOtherVoicePlayback(key) {
  const k = String(key);
  if (activeKey != null && String(activeKey) !== k) {
    const el = audioEl;
    if (el) el.pause();
    stopProgressLoop();
    activeKey = null;
    sourceUrl = null;
    loadStatus = "idle";
    pendingPlay = false;
    pendingResume = null;
    notify();
  }
}

export function seekVoicePlayback(key, time) {
  if (String(activeKey) !== String(key)) return;
  const el = audioEl;
  if (el && Number.isFinite(time)) {
    el.currentTime = time;
    notify();
  }
}

export function cycleVoiceSpeed() {
  const idx = SPEED_STEPS.findIndex((s) => s === speed);
  speed = SPEED_STEPS[idx >= 0 ? (idx + 1) % SPEED_STEPS.length : 0];
  if (audioEl) audioEl.playbackRate = speed;
  notify();
  return speed;
}
